import { useEffect, useState } from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { Card } from "../../components/Card";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { useI18n } from "../../lib/i18n";
import { useSession } from "../../lib/session";
import {
  auth,
  workspaces as wsAPI,
  ApiError,
  type WorkspaceMember,
  type WorkspaceMemberRole,
  type Workspace,
} from "../../lib/api";

// /app/settings — account + workspace management.
//
// Profile section is unchanged (display name + locale).
// Workspace section becomes editable in v0.3.0:
//   - rename + change timezone + change UI locale (owner/admin only)
//   - members list with role select + remove buttons
//   - add-member-by-email (must already be a registered account; the
//     handler returns 404 if the email is unknown, which we surface as
//     a clear i18n string — we never reveal whether an email is or
//     isn't in the system to a non-member).
//
// "Leave workspace" is just "remove self" — same DELETE endpoint, with
// a different confirm copy and a redirect to the workspace switcher
// after success.

const ROLES_ASSIGNABLE: WorkspaceMemberRole[] = ["admin", "editor", "approver", "viewer"];

function Settings() {
  const { t, locale, setLocale } = useI18n();
  const s = useSession();

  // Profile state (unchanged shape).
  const initialName = s.state === "signed" ? s.me.user.display_name : "";
  const [name, setName] = useState(initialName);
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileErr, setProfileErr] = useState<string | null>(null);

  // Workspace edit state. Hydrated from session.me.workspaces; refreshed
  // after a successful PATCH so the optimistic update reflects what the
  // server actually accepted.
  const sessionWs =
    s.state === "signed"
      ? s.me.workspaces.find((w) => w.id === s.me.current_workspace_id) ?? s.me.workspaces[0]
      : undefined;
  const [ws, setWs] = useState<Workspace | undefined>(sessionWs);
  const [wsName, setWsName] = useState(sessionWs?.name ?? "");
  const [wsTimezone, setWsTimezone] = useState(sessionWs?.timezone ?? "");
  const [wsLocale, setWsLocale] = useState(sessionWs?.locale ?? "");
  const [wsSubmitting, setWsSubmitting] = useState(false);
  const [wsSaved, setWsSaved] = useState(false);
  const [wsErr, setWsErr] = useState<string | null>(null);

  // Members state.
  const [members, setMembers] = useState<WorkspaceMember[] | null>(null);
  const [membersErr, setMembersErr] = useState<string | null>(null);

  // Add-member form.
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState<WorkspaceMemberRole>("editor");
  const [addingMember, setAddingMember] = useState(false);
  const [addMemberErr, setAddMemberErr] = useState<string | null>(null);
  const [addMemberInfo, setAddMemberInfo] = useState<string | null>(null);

  // Hydrate ws + members on mount or when the active workspace changes.
  // Note: we keep the session view in sync (workspaces.update calls
  // s.refresh after) so this effect only re-fires when the *id* changes.
  useEffect(() => {
    if (s.state !== "signed" || !sessionWs) return;
    let cancelled = false;
    setWs(sessionWs);
    setWsName(sessionWs.name);
    setWsTimezone(sessionWs.timezone);
    setWsLocale(sessionWs.locale);
    setMembers(null);
    setMembersErr(null);

    wsAPI
      .listMembers(sessionWs.id)
      .then((r) => {
        if (cancelled) return;
        setMembers(r.members);
      })
      .catch((e) => {
        if (cancelled) return;
        setMembersErr(t("common.errorGeneric") + " (" + String(e) + ")");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.state, sessionWs?.id]);

  if (s.state === "loading") return null;
  if (s.state === "anon") return <Navigate to="/login" replace />;
  const me = s.me;
  if (!ws || !sessionWs) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        <Card>
          <p className="text-secondary">{t("common.loading")}</p>
        </Card>
      </div>
    );
  }

  // Is the current user owner or admin of this workspace? Drives
  // visibility of the edit form + the "Add member" controls.
  const myMembership = members?.find((m) => m.user_id === me.user.id) ?? null;
  // While members is still loading, fall back to the owner_user_id
  // check from the workspace record — the workspace owner can always
  // self-identify without waiting for the roster.
  const isOwner = ws.owner_user_id === me.user.id;
  const isAdmin = myMembership?.role === "admin";
  const canEdit = isOwner || isAdmin;

  const profileDirty = name.trim() !== me.user.display_name && name.trim().length > 0;
  const wsDirty =
    wsName.trim() !== ws.name || wsTimezone.trim() !== ws.timezone || wsLocale.trim() !== ws.locale;

  async function onSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!profileDirty) return;
    setProfileSubmitting(true);
    setProfileErr(null);
    try {
      await auth.patchMe({ display_name: name.trim() });
      await s.refresh();
      setProfileSaved(true);
      window.setTimeout(() => setProfileSaved(false), 2000);
    } catch {
      setProfileErr(t("common.errorGeneric"));
    } finally {
      setProfileSubmitting(false);
    }
  }

  async function onSaveWorkspace(e: React.FormEvent) {
    e.preventDefault();
    if (!ws || !wsDirty) return;
    setWsSubmitting(true);
    setWsErr(null);
    try {
      const updated = await wsAPI.update(ws.id, {
        name: wsName.trim() !== ws.name ? wsName.trim() : undefined,
        timezone: wsTimezone.trim() !== ws.timezone ? wsTimezone.trim() : undefined,
        locale: wsLocale.trim() !== ws.locale ? wsLocale.trim() : undefined,
      });
      setWs(updated);
      // Pull the fresh workspace into the session so the global switcher
      // and other pages see the new name immediately.
      await s.refresh();
      setWsSaved(true);
      window.setTimeout(() => setWsSaved(false), 2000);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setWsErr(t("settings.error.forbidden"));
      } else {
        setWsErr(t("common.errorGeneric"));
      }
    } finally {
      setWsSubmitting(false);
    }
  }

  async function refreshMembers() {
    if (!ws) return;
    try {
      const r = await wsAPI.listMembers(ws.id);
      setMembers(r.members);
    } catch {
      // Non-fatal — the cached view stays. setMembersErr would be noisy
      // for a transient flake here.
    }
  }

  async function onAddMember(e: React.FormEvent) {
    e.preventDefault();
    if (!ws || !newMemberEmail.trim()) return;
    setAddingMember(true);
    setAddMemberErr(null);
    setAddMemberInfo(null);
    try {
      const result = await wsAPI.addMember(ws.id, {
        email: newMemberEmail.trim().toLowerCase(),
        role: newMemberRole,
      });
      // Server returns either a WorkspaceMember (existing user attached)
      // or an AddMemberInvite (email queued for an unregistered email).
      // The discriminator is the presence of `kind: "invited"`.
      if ("kind" in result && result.kind === "invited") {
        setAddMemberInfo(
          result.duplicate
            ? t("settings.invite.alreadyPending").replace("{email}", result.email)
            : t("settings.invite.sent").replace("{email}", result.email),
        );
      }
      setNewMemberEmail("");
      setNewMemberRole("editor");
      await refreshMembers();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setAddMemberErr(t("settings.error.alreadyMember"));
        } else if (err.status === 403) {
          setAddMemberErr(t("settings.error.forbidden"));
        } else {
          setAddMemberErr(t("common.errorGeneric"));
        }
      } else {
        setAddMemberErr(t("common.errorGeneric"));
      }
    } finally {
      setAddingMember(false);
    }
  }

  async function onChangeRole(member: WorkspaceMember, role: WorkspaceMemberRole) {
    if (!ws || role === member.role) return;
    try {
      const updated = await wsAPI.updateMemberRole(ws.id, member.user_id, { role });
      setMembers((cur) =>
        cur ? cur.map((m) => (m.user_id === member.user_id ? updated : m)) : cur,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setMembersErr(t("settings.error.forbidden"));
      } else {
        setMembersErr(t("common.errorGeneric"));
      }
    }
  }

  async function onRemoveMember(member: WorkspaceMember) {
    if (!ws) return;
    const isSelf = member.user_id === me.user.id;
    const confirmMsg = isSelf
      ? t("settings.memberLeaveConfirm")
      : t("settings.memberRemoveConfirm");
    if (!window.confirm(confirmMsg)) return;
    try {
      await wsAPI.removeMember(ws.id, member.user_id);
      if (isSelf) {
        // After leaving, the SPA's view of /v1/auth/me changes — workspace
        // disappears from the list, current_workspace_id may bounce. A
        // full refresh + redirect to /app makes the state transition
        // unambiguous.
        await s.refresh();
        window.location.href = "/app";
        return;
      }
      await refreshMembers();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setMembersErr(t("settings.error.cannotRemoveOwner"));
      } else if (err instanceof ApiError && err.status === 403) {
        setMembersErr(t("settings.error.forbidden"));
      } else {
        setMembersErr(t("common.errorGeneric"));
      }
    }
  }

  // Role labels are translated via the i18n keys settings.memberRole.<role>.
  function roleLabel(r: WorkspaceMemberRole): string {
    return t(`settings.memberRole.${r}`);
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12 flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <span className="eyebrow">{t("app.settings")}</span>
        <h1 className="font-display text-3xl font-semibold">{t("settings.title")}</h1>
      </div>

      {/* Profile card */}
      <Card>
        <h2 className="font-display text-lg font-semibold mb-1">{t("settings.profile")}</h2>
        <form onSubmit={onSaveProfile} className="flex flex-col gap-4 mt-4" noValidate>
          <Input
            label={t("settings.email")}
            type="email"
            value={me.user.email}
            readOnly
            disabled
            hint={t("settings.emailNote")}
          />
          <Input
            label={t("settings.displayName")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
          />
          <div className="flex flex-col gap-1.5">
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">
              {t("settings.locale")}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setLocale("tr")}
                aria-pressed={locale === "tr"}
                className={`ring-nexus rounded-[10px] px-4 py-2 text-sm border transition ${
                  locale === "tr"
                    ? "bg-red text-white border-transparent"
                    : "bg-elevated border-border-default text-secondary hover:text-primary"
                }`}
              >
                Türkçe
              </button>
              <button
                type="button"
                onClick={() => setLocale("en")}
                aria-pressed={locale === "en"}
                className={`ring-nexus rounded-[10px] px-4 py-2 text-sm border transition ${
                  locale === "en"
                    ? "bg-red text-white border-transparent"
                    : "bg-elevated border-border-default text-secondary hover:text-primary"
                }`}
              >
                English
              </button>
            </div>
          </div>

          {profileErr ? (
            <p className="text-error text-sm" role="alert">
              {profileErr}
            </p>
          ) : null}
          <div className="flex items-center gap-3 mt-2">
            <Button type="submit" loading={profileSubmitting} disabled={!profileDirty}>
              {t("settings.save")}
            </Button>
            {profileSaved ? (
              <span className="text-success text-sm" role="status">
                ✓ {t("settings.saved")}
              </span>
            ) : null}
          </div>
        </form>
      </Card>

      {/* Workspace card — editable for owner/admin, read-only otherwise. */}
      <Card>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-lg font-semibold">{t("settings.workspace")}</h2>
          {canEdit ? (
            <span className="text-xs text-muted">{t("settings.workspaceEditHint")}</span>
          ) : null}
        </div>
        <form onSubmit={onSaveWorkspace} className="flex flex-col gap-4">
          <Input
            label={t("settings.workspaceName")}
            value={wsName}
            onChange={(e) => setWsName(e.target.value)}
            disabled={!canEdit}
            maxLength={100}
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label={t("settings.workspaceTimezone")}
              value={wsTimezone}
              onChange={(e) => setWsTimezone(e.target.value)}
              disabled={!canEdit}
              hint="e.g. Europe/Istanbul"
            />
            <Input
              label={t("settings.workspaceLocale")}
              value={wsLocale}
              onChange={(e) => setWsLocale(e.target.value)}
              disabled={!canEdit}
              hint="e.g. tr-TR"
            />
          </div>
          <Input
            label={t("settings.workspaceSlug")}
            value={ws.slug}
            disabled
            readOnly
          />
          {wsErr ? (
            <p className="text-error text-sm" role="alert">
              {wsErr}
            </p>
          ) : null}
          {canEdit ? (
            <div className="flex items-center gap-3 mt-1">
              <Button type="submit" loading={wsSubmitting} disabled={!wsDirty}>
                {t("settings.save")}
              </Button>
              {wsSaved ? (
                <span className="text-success text-sm" role="status">
                  ✓ {t("settings.workspaceUpdated")}
                </span>
              ) : null}
            </div>
          ) : null}
        </form>
      </Card>

      {/* Members card */}
      <Card>
        <h2 className="font-display text-lg font-semibold mb-1">{t("settings.members")}</h2>
        <p className="text-secondary text-sm mb-4">{t("settings.membersSubtitle")}</p>

        {/* Roster */}
        {members === null ? (
          <p className="text-secondary text-sm">{t("common.loading")}</p>
        ) : members.length === 0 ? (
          <p className="text-secondary text-sm">{t("settings.membersEmpty")}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border-default">
            {members.map((m) => {
              const self = m.user_id === me.user.id;
              const editableRole =
                canEdit && m.role !== "owner" && (!self || isOwner) && (isOwner || m.role !== "admin");
              const canRemove =
                m.role !== "owner" &&
                (self || (canEdit && (isOwner || m.role !== "admin")));
              return (
                <li
                  key={m.user_id}
                  className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-primary truncate">
                      {m.display_name}{" "}
                      {self ? (
                        <span className="text-muted text-xs">{t("settings.memberSelf")}</span>
                      ) : null}
                    </p>
                    <p className="text-secondary text-xs truncate">{m.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {editableRole ? (
                      <Select
                        label=""
                        value={m.role}
                        onChange={(e) =>
                          void onChangeRole(m, e.target.value as WorkspaceMemberRole)
                        }
                        options={ROLES_ASSIGNABLE.map((r) => ({ value: r, label: roleLabel(r) }))}
                        className="min-w-[8rem]"
                      />
                    ) : (
                      <span className="font-mono text-xs uppercase tracking-[0.12em] text-secondary px-2 py-1 border border-border-default rounded-md">
                        {roleLabel(m.role)}
                      </span>
                    )}
                    {canRemove ? (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => void onRemoveMember(m)}
                      >
                        {self ? t("settings.memberLeave") : t("settings.memberRemove")}
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {membersErr ? (
          <p className="text-error text-sm mt-3" role="alert">
            {membersErr}
          </p>
        ) : null}

        {/* Add-member form — owner/admin only. */}
        {canEdit ? (
          <form
            onSubmit={onAddMember}
            className="mt-6 pt-6 border-t border-border-default flex flex-col gap-3"
            noValidate
          >
            <h3 className="font-display text-base font-semibold">{t("settings.addMember")}</h3>
            <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
              <Input
                label={t("settings.addMemberEmail")}
                type="email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder="teammate@example.com"
                autoComplete="off"
              />
              <Select
                label={t("settings.addMemberRole")}
                value={newMemberRole}
                onChange={(e) => setNewMemberRole(e.target.value as WorkspaceMemberRole)}
                options={ROLES_ASSIGNABLE.map((r) => ({ value: r, label: roleLabel(r) }))}
              />
              <Button
                type="submit"
                loading={addingMember}
                disabled={!newMemberEmail.trim()}
              >
                {t("settings.addMemberCta")}
              </Button>
            </div>
            {addMemberErr ? (
              <p className="text-error text-sm" role="alert">
                {addMemberErr}
              </p>
            ) : null}
            {addMemberInfo ? (
              <p className="text-success text-sm" role="status">
                {addMemberInfo}
              </p>
            ) : null}
          </form>
        ) : null}
      </Card>

      <Card>
        <Button variant="secondary" onClick={() => void s.signOut()}>
          {t("settings.signOut")}
        </Button>
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/app/settings")({
  component: Settings,
});
